import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { numberRangesApi } from '../../services/api';
import { toast } from 'sonner';
import { Tag, Plus, RefreshCw, Pencil } from 'lucide-react';

const DOC_TYPE_LABELS = {
  PR: 'Purchase Requisition', PO: 'Purchase Order', GR: 'Goods Receipt',
  IR: 'Invoice Receipt', FI: 'FI Document', VP: 'Vendor Payment',
};

const EMPTY = {
  range_id: '01', document_type: '', description: '', prefix: '',
  from_number: 1, to_number: 999999999, current_number: 1, number_length: 10, fiscal_year: '',
};

const pad = (num, len) => String(num).padStart(len || 10, '0');

export default function NumberRanges() {
  const [ranges, setRanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await numberRangesApi.list();
      setRanges(res.data || []);
    } catch (e) {
      toast.error('Failed to load number ranges');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ ...r, fiscal_year: r.fiscal_year || '', prefix: r.prefix || '', description: r.description || '' });
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (editing) {
        // Only description + current_number are updatable
        await numberRangesApi.update(editing.range_id, editing.document_type, {
          description: form.description,
          current_number: parseInt(form.current_number),
        });
        toast.success('Number range updated');
      } else {
        if (!form.document_type.trim()) { toast.error('Document type is required'); return; }
        await numberRangesApi.create({
          range_id: form.range_id || '01',
          document_type: form.document_type.toUpperCase(),
          description: form.description || null,
          prefix: form.prefix || null,
          from_number: parseInt(form.from_number),
          to_number: parseInt(form.to_number),
          current_number: parseInt(form.current_number),
          number_length: parseInt(form.number_length),
          fiscal_year: form.fiscal_year || null,
        });
        toast.success('Number range created');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="number-ranges-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Tag className="w-7 h-7 text-orange-600" />
              Document Number Ranges
            </h1>
            <p className="text-gray-600 mt-1">
              Configure the number ranges that generate document IDs (PR, PO, GR, IR, FI, Vendor Payment, …).
              Ranges cannot be deleted once in use; you can adjust the description or current counter.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} data-testid="nr-refresh"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
            <Button onClick={openCreate} data-testid="nr-new-btn"><Plus className="w-4 h-4 mr-1" />New Range</Button>
          </div>
        </div>

        <Card>
          <CardHeader><CardTitle>{ranges.length} Number Range(s)</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-10 text-gray-500">Loading…</div>
            ) : ranges.length === 0 ? (
              <div className="text-center py-10 text-gray-500">No number ranges configured.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Doc Type</TableHead>
                    <TableHead>Range ID</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Prefix</TableHead>
                    <TableHead className="text-right">From</TableHead>
                    <TableHead className="text-right">To</TableHead>
                    <TableHead className="text-right">Current</TableHead>
                    <TableHead>Next Number</TableHead>
                    <TableHead>FY</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ranges.map((r) => (
                    <TableRow key={`${r.document_type}-${r.range_id}-${r.fiscal_year || ''}`} data-testid={`nr-row-${r.document_type}`}>
                      <TableCell>
                        <Badge variant="outline">{r.document_type}</Badge>
                        <div className="text-xs text-gray-400">{DOC_TYPE_LABELS[r.document_type] || ''}</div>
                      </TableCell>
                      <TableCell className="font-mono">{r.range_id}</TableCell>
                      <TableCell>{r.description || '—'}</TableCell>
                      <TableCell className="font-mono">{r.prefix || '—'}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{r.from_number?.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{r.to_number?.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono">{r.current_number?.toLocaleString()}</TableCell>
                      <TableCell className="font-mono text-orange-600 text-xs">
                        {(r.prefix || '')}{pad((r.current_number || 0) + 1, r.number_length)}
                      </TableCell>
                      <TableCell>{r.fiscal_year || '—'}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`nr-edit-${r.document_type}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent data-testid="nr-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? `Edit ${editing.document_type} Range` : 'New Number Range'}</DialogTitle>
              <DialogDescription>
                {editing ? 'Only the description and current counter can be changed.' : 'Define a new document number range.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Document Type *</Label>
                  <Input
                    value={form.document_type}
                    disabled={!!editing}
                    placeholder="e.g. VP"
                    onChange={(e) => setForm({ ...form, document_type: e.target.value.toUpperCase() })}
                    data-testid="nr-doctype"
                  />
                </div>
                <div>
                  <Label>Range ID</Label>
                  <Input value={form.range_id} disabled={!!editing} onChange={(e) => setForm({ ...form, range_id: e.target.value })} data-testid="nr-rangeid" />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="nr-description" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Prefix</Label>
                  <Input value={form.prefix} disabled={!!editing} maxLength={4} onChange={(e) => setForm({ ...form, prefix: e.target.value.toUpperCase() })} data-testid="nr-prefix" />
                </div>
                <div>
                  <Label>Number Length</Label>
                  <Input type="number" value={form.number_length} disabled={!!editing} onChange={(e) => setForm({ ...form, number_length: e.target.value })} data-testid="nr-length" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>From</Label>
                  <Input type="number" value={form.from_number} disabled={!!editing} onChange={(e) => setForm({ ...form, from_number: e.target.value })} data-testid="nr-from" />
                </div>
                <div>
                  <Label>To</Label>
                  <Input type="number" value={form.to_number} disabled={!!editing} onChange={(e) => setForm({ ...form, to_number: e.target.value })} data-testid="nr-to" />
                </div>
                <div>
                  <Label>Current</Label>
                  <Input type="number" value={form.current_number} onChange={(e) => setForm({ ...form, current_number: e.target.value })} data-testid="nr-current" />
                </div>
              </div>
              <div>
                <Label>Fiscal Year (optional)</Label>
                <Input value={form.fiscal_year} disabled={!!editing} maxLength={4} placeholder="leave blank for non-year-specific" onChange={(e) => setForm({ ...form, fiscal_year: e.target.value })} data-testid="nr-fy" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="nr-save">{saving ? 'Saving…' : (editing ? 'Update' : 'Create')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
